/**
 * Founder Control Center · Section 9 · Notifications · Sprint T-17.1.
 *
 * Single source of truth for Notification Center. Consumes the 19 endpoints of
 * the Notification Dispatcher (T-17) via React Query · 1 endpoint = 1 hook.
 *
 * 8 sub-tabs (§4 of design):
 *   1. Overview          → OverviewTab
 *   2. Deliveries        → DeliveriesTab (+ drawer)
 *   3. DLQ               → DlqTab
 *   4. Templates         → TemplatesTab (+ drawer, overrides read-only)
 *   5. Preferences       → PreferencesTab (/preferences/me only)
 *   6. Webhook Endpoints → WebhookEndpointsTab
 *   7. Providers Health  → ProvidersHealthTab
 *   8. Metrics           → MetricsTab (SVG puro · derived from deliveries)
 *
 * Filosofía T-17.1:
 *   - 0 lógica de negocio en React
 *   - 1 endpoint = 1 hook = 1 widget
 *   - 0 dependencias nuevas
 *   - 0 modificaciones al backend (T-17 permanece intacto)
 *   - AI Platform Freeze 100%
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BarChart3, Send, AlertOctagon, FileText, User,
  Webhook, Activity, Gauge, Bell, PlayCircle, LineChart, ArrowRightLeft,
  ChartLine,
} from "lucide-react";
import SubTabs, { useSubTab } from "./SubTabs";
import OverviewTab from "../../../components/notificationCenter/OverviewTab";
import DeliveriesTab from "../../../components/notificationCenter/DeliveriesTab";
import DlqTab from "../../../components/notificationCenter/DlqTab";
import TemplatesTab from "../../../components/notificationCenter/TemplatesTab";
import PreferencesTab from "../../../components/notificationCenter/PreferencesTab";
import WebhookEndpointsTab from "../../../components/notificationCenter/WebhookEndpointsTab";
import ProvidersHealthTab from "../../../components/notificationCenter/ProvidersHealthTab";
import MetricsTab from "../../../components/notificationCenter/MetricsTab";
import PlaygroundTab from "../../../components/notificationCenter/PlaygroundTab";
import AnalyticsTab from "../../../components/notificationCenter/AnalyticsTab";
import LegacyMigrationTab from "../../../components/notificationCenter/LegacyMigrationTab";
// Sprint T-23 · Analytics interno (engagement/delivery/routing/latencia)
import NotificationAnalyticsTab from "../../../components/notificationCenter/NotificationAnalyticsTab";

// Sprint T-18 · Notification Studio · Q1=b · visual grouping in 3 clusters:
//   [Operations]      Overview · Deliveries · DLQ · Metrics
//   [Studio · T-18]   Playground · Analytics · Legacy Migration
//                     (Template Studio unified into Templates tab · Q8=c)
//   [Config]          Templates · Preferences · Webhooks · Providers
const ITEMS = [
  { key: "overview",       label: "Overview",     icon: BarChart3 },
  { key: "deliveries",     label: "Deliveries",   icon: Send },
  { key: "dlq",            label: "DLQ",          icon: AlertOctagon },
  { key: "metrics",        label: "Metrics",      icon: Gauge },
  // Sprint T-23 · Analytics interno (delivery/engagement/routing/latencia)
  { key: "engagement",     label: "Engagement",   icon: ChartLine },
  { type: "separator" },
  { key: "playground",     label: "Playground",   icon: PlayCircle },
  { key: "analytics",      label: "Analytics",    icon: LineChart },
  { key: "legacy",         label: "Legacy",       icon: ArrowRightLeft },
  { type: "separator" },
  { key: "templates",      label: "Templates",    icon: FileText },
  { key: "preferences",    label: "Preferences",  icon: User },
  { key: "endpoints",      label: "Webhooks",     icon: Webhook },
  { key: "providers",      label: "Providers",    icon: Activity },
];

const ALL_KEYS = ITEMS.filter((i) => i.key).map((i) => i.key);

export default function NotificationsSection() {
  const [sub, setSub] = useSubTab("overview");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const current = searchParams.get("sub");
    if (current && !ALL_KEYS.includes(current)) {
      navigate("/founder/notifications?sub=overview", { replace: true });
    }
  }, [searchParams, navigate]);

  const goToTab = (key) => setSub(key);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-5" data-testid="fcc-notifications-section">
      <Header />
      <SubTabs items={ITEMS} value={sub} onChange={setSub} testid="notifications-subtabs" />
      <div className="pt-2">
        {sub === "overview"    && <OverviewTab onNavigate={goToTab} />}
        {sub === "deliveries"  && <DeliveriesTab />}
        {sub === "dlq"         && <DlqTab />}
        {sub === "templates"   && <TemplatesTab />}
        {sub === "preferences" && <PreferencesTab />}
        {sub === "endpoints"   && <WebhookEndpointsTab />}
        {sub === "providers"   && <ProvidersHealthTab />}
        {sub === "metrics"     && <MetricsTab />}
        {sub === "engagement"  && <NotificationAnalyticsTab />}
        {sub === "playground"  && <PlaygroundTab />}
        {sub === "analytics"   && <AnalyticsTab />}
        {sub === "legacy"      && <LegacyMigrationTab />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
        <Bell size={11} /> Founder Control Center · Notifications
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
        Notification Center
      </h1>
      <p className="text-xs text-[var(--ag-text-dim)] mt-1">
        Operations · Studio · Config
      </p>
    </div>
  );
}
